import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPublishedGame, sendRun } from '../api/playAPI';
import { getGuestSessionId } from '../../utils/guestSession';
import MissionGame from './MissionGame';
import './GameBuilder.css';

/**
 * A published game, opened from its share link (/play/<token>) - no account needed. The player may give a first
 * name for the trainer's report; each play is sent when it ends (won, lost, or left part way) with a random id this
 * browser tab made up (utils/guestSession.js), never anything that identifies them.
 *
 * Opened by a SCORM package inside an LMS (?lms=scorm, backend services/scorm.py), the name is the LMS's (?learner=) and
 * each play's end is also posted to the package's page, which reports passed / failed to the LMS.
 */
export const RUN_MESSAGE = 'simulatejob:run';
export default function GuestPlay() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const lms = search.get('lms') === 'scorm';
  const learner = lms ? (search.get('learner') || '').trim().slice(0, 40) : '';
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState(learner);
  const [playing, setPlaying] = useState(false);
  const [plays, setPlays] = useState(0);

  useEffect(() => {
    let live = true;
    getPublishedGame(token).then((g) => live && setGame(g)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [token]);

  const onRun = (run) => {
    setPlays((n) => n + 1);
    sendRun(token, { ...run, guest: getGuestSessionId() || '', name: name.trim(), version: game.version });
    if (lms && window.parent !== window) {
      window.parent.postMessage({ type: RUN_MESSAGE, outcome: run.outcome, seconds: run.seconds, version: game.version }, '*');
    }
  };

  if (error) return <main className="gb-guest"><div className="gb-panel" role="alert"><h2>Game not found</h2><p>{error}</p></div></main>;
  if (!game) return <main className="gb-guest"><p className="gb-muted">Loading the game…</p></main>;
  return (
    <main className="gb-guest">
      <div className="gb-panel">
        <h2>{game.title || game.spec.mission.name}</h2>
        <p>{game.spec.objective.join(' ')}</p>
        {learner ? <p className="gb-small">Playing as <strong>{learner}</strong> - your result goes to your learning system.</p> : (
          <label className="gb-flow-row">
            <span>Your first name <span className="gb-muted">(optional - only your trainer sees it)</span></span>
            <input type="text" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} aria-label="Your first name" />
          </label>
        )}
        <div className="gb-actions">
          <button type="button" className="gb-btn" onClick={() => setPlaying(true)}>▶ {plays ? 'Play again' : 'Start'}</button>
        </div>
        <p className="gb-small gb-muted">No account needed. How you play (what you did, how long it took) is shared with whoever made this game.</p>
      </div>
      {playing && <MissionGame spec={game.spec} page onClose={() => setPlaying(false)} onRun={onRun} />}
    </main>
  );
}
